/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react-hooks/rules-of-hooks */
import {
  Button,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import FactCheckRounded from '@mui/icons-material/FactCheckRounded'
import HighlightOffRounded from '@mui/icons-material/HighlightOffRounded'
import { FC, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'

import {
  fetchBillsBwDatesAction,
  fetchMonthlyBillResetAction,
  fetchMonthlyBillsAction,
} from '../../../store/monthlyBills'
import { DeleteMonthlyBill } from './DeleteMonthlyBill'
import {
  currencyFormatter,
  getDisplayDateTime,
  getShortFormattedDate,
} from '../../../utils'

import { CreateMonthlyBill } from './CreateMonthlyBill'
import { EmptyWithAddButton } from '../../../components/Empty/EmptyWithAddButton'
import { Bill, MonthlyBill, MonthlyBillsResponse } from 'src/services'
import { BillSettings } from '../slips/BillSettings'
import { BarButton } from '../slips/BarButton'
import { CreateBill } from './CreateBill'
import { ConfirmationAppDialog, Loader } from '../../../components'
import { adjustBillingGap, fetchInvoicePdf } from '../../../services/monthlyBills'
import { GenerateEInvoice } from '../slips/einvoice/GenerateEInvoice'
import { CancelEInvoice } from '../slips/einvoice/CancelEInvoice'
import { DebitCreditNoteDialog } from './DebitCreditNoteDialog'
import {
  BILL_TABLE_ROW_HEIGHT,
  NOTE_ROW_DIVIDER_COLOR,
  NOTE_ROW_DIVIDER_INDENT_PX,
  IntervalNameCell,
  buildBillTreeLinkMap,
  getBillRowClassName,
  getGenerateEInvoiceTooltip,
  isCancelledBillRow,
  isDebitCreditNoteRow,
  isGstBillRow,
} from './billRowShared'
import { bulkInvoiceAction, BulkInvoiceAction } from '../AllBills/Services'
import { BulkInvoiceActionConfirm } from '../AllBills/BulkInvoiceActionConfirm'

export interface MonthlyBillsProps {
  bill?: Bill,
  gst?: boolean
  finYear?: string
}

const BILLING_STATUS_BANNER = {
  ERROR: {
    backgroundColor: 'rgba(199, 66, 98, 0.12)',
    borderColor: 'rgba(199, 66, 98, 0.24)',
    textColor: '#8B3048',
    iconBg: '#C74262',
    buttonColor: '#B5475C',
    buttonBorder: 'rgba(199, 66, 98, 0.42)',
    buttonHoverColor: '#A63F57',
    buttonHoverBg: 'rgba(199, 66, 98, 0.10)',
  },

  SUCCESS: {
    backgroundColor: 'rgba(36, 155, 91, 0.12)',
    borderColor: 'rgba(36, 155, 91, 0.24)',
    textColor: '#1E6B43',
    iconBg: '#249B5B',
    buttonColor: '#249B5B',
    buttonBorder: 'rgba(36, 155, 91, 0.42)',
    buttonHoverColor: '#1E824C',
    buttonHoverBg: 'rgba(36, 155, 91, 0.10)',
  },

  WARNING: {
    backgroundColor: 'rgba(255, 163, 25, 0.12)',
    borderColor: 'rgba(255, 163, 25, 0.28)',
    textColor: '#7A4D0A',
    iconBg: '#FFA319',
    buttonColor: '#B8830A',
    buttonBorder: 'rgba(255, 163, 25, 0.42)',
    buttonHoverColor: '#996600',
    buttonHoverBg: 'rgba(255, 163, 25, 0.10)',
  },
} as const

interface BillingStatusBannerProps {
  message: string
  messageType?: string
  showFixError?: boolean
  onFixError: () => void
  onDismiss: () => void
}

const BillingStatusBanner: FC<BillingStatusBannerProps> = ({
  message,
  messageType,
  showFixError,
  onFixError,
  onDismiss,
}) => {
  const isError = messageType === 'ERROR'
  const isWarning = messageType === 'WARNING'
  const styles = isError
    ? BILLING_STATUS_BANNER.ERROR
    : isWarning
      ? BILLING_STATUS_BANNER.WARNING
      : BILLING_STATUS_BANNER.SUCCESS
  const StatusIcon = isError
    ? ErrorOutlineIcon
    : isWarning
      ? ErrorOutlineIcon
      : CheckCircleOutlineIcon

  return (
    <Box
      sx={{
        px: 2,
        py: 0.8,
        my: 0.5,
        mx:0.1,
        borderRadius: '6px',
        backgroundColor: styles.backgroundColor,
        border: `1px solid ${styles.borderColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: styles.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <StatusIcon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
        <Typography variant="body2" sx={{ color: styles.textColor, lineHeight: 1.45 }}>
          {message}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flexShrink: 0 }}
      >
        {showFixError && (
          <Button
            size="small"
            variant="outlined"
            onClick={onFixError}
            sx={{
              whiteSpace: 'nowrap',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              minHeight: 32,
              py: 0.5,
              px: 2,
              lineHeight: 1.2,
              borderRadius: 9999,
              color: styles.buttonColor,
              borderColor: styles.buttonBorder,
              backgroundColor: 'transparent',
              '&:hover': {
                borderColor: styles.buttonHoverColor,
                color: styles.buttonHoverColor,
                backgroundColor: styles.buttonHoverBg,
              },
            }}
          >
            Fix Error
          </Button>
        )}
        <IconButton
          size="small"
          aria-label="Dismiss"
          onClick={onDismiss}
          sx={{
            color: '#9CA3AF',
            '&:hover': {
              color: '#6B7280',
              backgroundColor: 'transparent',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  )
}

export const MonthlyBills: FC<MonthlyBillsProps> = ({ bill, finYear, gst }) => {
  const dispatchAction = useAppDispatch()
  const {
    success,
    data: data,
    message,
  } = useAppSelector((state) => state.fetchMonthlyBill)

  const {
    success: allSuccess,
    data: allData,
    message: allMessage,
  } = useAppSelector((state) => state.fetchAllBills)

  const [showCreateMonthlyBill, setShowCreateMonthlyBill] = useState(false)
  const [showBillSettings, setShowBillSettings] = useState(false)
  const [selectedMonthlyBill, setSelectedMonthlyBill] = useState<MonthlyBill>(null)
  const [showCreateBill, setShowCreateBill] = useState(false)
  const [billsResponse, setBillsResponse] = useState<MonthlyBillsResponse>(null)
  const [showAdjustDialog, setShowAdjustDialog] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const [eInvoiceBill, setEInvoiceBill] = useState<MonthlyBill | null>(null)
  const [cancelEInvoiceBill, setCancelEInvoiceBill] = useState<MonthlyBill | null>(null)
  const [viewPdfLoading, setViewPdfLoading] = useState(false)
  const [noteMenuAnchor, setNoteMenuAnchor] = useState<HTMLElement | null>(null)
  const [noteMenuBill, setNoteMenuBill] = useState<MonthlyBill | null>(null)
  const [noteDialog, setNoteDialog] = useState<{
    invoiceId: number
    noteType: 'DBN' | 'CRN'
    accountId: number
  } | null>(null)
  const [showMismatchBanner, setShowMismatchBanner] = useState(true)
  const [emailActionInvoiceIds, setEmailActionInvoiceIds] = useState<
    number[] | null
  >(null)
  const [pendingBulkAction, setPendingBulkAction] =
    useState<BulkInvoiceAction | null>(null)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [bulkActionLoading, setBulkActionLoading] = useState(false)

  const handleViewBillPdf = async (invoiceId: number) => {
    setViewPdfLoading(true)
    try {
      const response = await fetchInvoicePdf(invoiceId)
      if (response?.success && response.data) {
        window.open(response.data, '_blank')
      } else {
        dispatchAction(
          showAlertAction({
            message: response?.message ?? 'Failed to load invoice PDF',
            severity: 'error',
          })
        )
      }
    } catch {
      dispatchAction(
        showAlertAction({
          message: 'Failed to load invoice PDF',
          severity: 'error',
        })
      )
    } finally {
      setViewPdfLoading(false)
    }
  }

  useEffect(() => {
    dispatchAction(fetchMonthlyBillResetAction())
  }, [])

  useEffect(() => {
    if(bill) {
      // Clear any existing data when loading for a specific account
      setBillsResponse(null)
      dispatchAction(fetchMonthlyBillsAction(bill?.id))
    }
  }, [bill])

  useEffect(() => {
    if (!success && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
    }
    if(data) {
      setBillsResponse(data)
    }
  }, [message, data])

  useEffect(() => {
    if (!allSuccess && allMessage) {
      dispatchAction(showAlertAction({ message: allMessage, severity: 'error' }))
    }
    // Only use allData if we're not in a specific account context (bill prop is null/undefined)
    if(allData && !bill) {
      setBillsResponse(allData)
    }
  }, [allMessage, allData, bill])

  useEffect(() => {
    if(finYear && gst != null && gst != undefined) {
      fetchBills()
    }
  }, [finYear, gst])

  useEffect(() => {
    if (billsResponse?.totalMismatchWarning) {
      setShowMismatchBanner(true)
    }
  }, [billsResponse?.totalMismatchWarning])

  useEffect(() => {
    if (!success && message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
    }
  }, [message])

  function fetchBills() {
    dispatchAction(fetchBillsBwDatesAction(finYear, gst))
  }

  const reloadBillsList = () => {
    if (bill?.id) {
      dispatchAction(fetchMonthlyBillsAction(bill.id))
    } else {
      fetchBills()
    }
  }

  const handleSendEmailBadgeClick = (invoiceId: number) => {
    setEmailActionInvoiceIds([invoiceId])
    setPendingBulkAction('SEND_EMAIL')
    setShowBulkConfirm(true)
  }

  const handleBulkActionConfirm = async () => {
    if (!pendingBulkAction || !emailActionInvoiceIds) {
      return
    }
    setBulkActionLoading(true)
    try {
      const response = await bulkInvoiceAction({
        invoiceIds: emailActionInvoiceIds,
        action: pendingBulkAction,
      })
      if (response?.success) {
        dispatchAction(
          showAlertAction({
            message: response.message ?? 'Done',
            severity: 'success',
          })
        )
        reloadBillsList()
      } else {
        dispatchAction(
          showAlertAction({
            message: response?.message ?? 'Action failed',
            severity: 'error',
          })
        )
      }
    } catch {
      dispatchAction(
        showAlertAction({
          message: 'Action failed',
          severity: 'error',
        })
      )
    } finally {
      setBulkActionLoading(false)
      setShowBulkConfirm(false)
      setPendingBulkAction(null)
      setEmailActionInvoiceIds(null)
    }
  }

  const adjustBillingGapHandler = async () => {
    if (!bill?.id || adjusting) {
      return
    }
    setAdjusting(true)
    try {
      const response = await adjustBillingGap(bill.id)
      const result = response?.data
      if (result?.success && result?.data) {
        setBillsResponse(result.data)
        dispatchAction(
          showAlertAction({
            message: 'Invoice difference adjusted successfully.',
            severity: 'success',
          })
        )
        setShowAdjustDialog(false)
      } else {
        dispatchAction(
          showAlertAction({
            message: result?.message || 'Unable to adjust invoice difference.',
            severity: 'error',
          })
        )
      }
    } catch (error) {
      dispatchAction(
        showAlertAction({
          message: 'Unable to adjust invoice difference.',
          severity: 'error',
        })
      )
    } finally {
      setAdjusting(false)
    }
  }

  const billTreeLinkMap = useMemo(
    () => buildBillTreeLinkMap(billsResponse?.bills ?? []),
    [billsResponse?.bills]
  )

  const columns: GridColDef[] = [
    {
      field: 'intervalName',
      sortable: false,
      headerName: 'Month',
      width: 100,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (value) => (
        <IntervalNameCell
          row={value.row as MonthlyBill}
          link={billTreeLinkMap.get(value.row.id)}
        />
      ),
    },
    {
      field: 'displayInvoiceNo',
      headerName: 'Invoice No',
      width: 100,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (value) => {
        const dataCy = `id_typo_bill_${value.row.intervalName}_invoiceNo`
        return (
          <Typography data-cy={dataCy}>
            {value.row.displayInvoiceNo ? value.row.displayInvoiceNo : value.row.invoiceNo}
          </Typography>
        )
      },
    },
    {
      field: 'createDate',
      sortable: false,
      headerName: 'Invoice Date',
      width: 130,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params) => {
        const dataCy = `id_typo_bill_${params.row.intervalName}_invoiceDate`
        const displayValue = params.row.createDate
          ? getShortFormattedDate(params.row.createDate)
          : ''
        return (
          <Typography data-cy={dataCy}>
            {displayValue}
          </Typography>
        )
      },
      valueGetter: (params) => {
        return params.row.createDate
          ? getShortFormattedDate(params.row.createDate)
          : ''
      },
    },
    {
      field: 'startDate',
      sortable: false,
      headerName: 'Start Date',
      width: 100,
      headerClassName: 'super-app-theme--header',
      flex: 1,

      renderCell: (params) => {
        if (isDebitCreditNoteRow(params.row)) {
          return (
            <Typography data-cy={`id_typo_bill_${params.row.intervalName}_startDate`}>
              -
            </Typography>
          )
        }
        const startDate = params.row.startDate ? getShortFormattedDate(params.row.startDate) : ''
        const dataCy = `id_typo_bill_${params.row.intervalName}_startDate`
        return (
          <Typography data-cy={dataCy}>
            {startDate}
          </Typography>
        )
      },
      valueGetter: (params) => {
        if (isDebitCreditNoteRow(params.row)) {
          return '-'
        }
        return params.row.startDate
          ? getShortFormattedDate(params.row.startDate)
          : ''
      },
    },
    {
      field: 'endDate',
      sortable: false,
      headerName: 'End Date',
      width: 100,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params) => {
        if (isDebitCreditNoteRow(params.row)) {
          return (
            <Typography data-cy={`id_typo_bill_${params.row.intervalName}_endDate`}>
              -
            </Typography>
          )
        }
        const endDate = params.row.endDate ? getShortFormattedDate(params.row.endDate) : ''
        const dataCy = `id_typo_bill_${params.row.intervalName}_endDate`
        return (
          <Typography data-cy={dataCy}>
            {endDate}
          </Typography>
        )
      },
      valueGetter: (params) => {
        if (isDebitCreditNoteRow(params.row)) {
          return '-'
        }
        return params.row.endDate
          ? getShortFormattedDate(params.row.endDate)
          : ''
      },
    },
    {
      field: 'invoiceTotal',
      headerName: 'Invoice Total',
      headerAlign: 'left',
      align: 'left',
      type: 'number',
      width: 100,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (value) => {
        const dataCy = `id_typo_bill_${value.row.intervalName}_invoiceTotal`
        return (
          <Typography fontWeight={value.row.id < 0 ? 'bold' : '400'} data-cy={dataCy}>
            {currencyFormatter.format(value.row.invoiceTotal)}
          </Typography>
        )
      },
    },
    {
      field: 'actualCreateDate',
      sortable: false,
      headerName: 'Create Date',
      width: 130,
      headerClassName: 'super-app-theme--header',
      flex: 1,
      renderCell: (params) => {
        const dataCy = `id_typo_bill_${params.row.intervalName}_createDate`
        const displayValue = params.row.createDate
          ? getDisplayDateTime(params.row.actualCreateDate)
          : ''
        return (
          <Typography data-cy={dataCy}>
            {displayValue}
          </Typography>
        )
      },
      valueGetter: (params) => {
        return params.row.createDate
          ? getDisplayDateTime(params.row.actualCreateDate)
          : ''
      },
    },
    {
      field: 'none',
      headerName: 'Actions  ',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      width: 244,
      minWidth: 244,
      flex: 0,
      align: 'right',
      headerClassName: 'super-app-theme--header',

      renderCell: (value) => {
        const [showDeleteDailog, setShowDeleteDailog] = useState(false)
        const row: MonthlyBill = value.row
        const showGstActions = isGstBillRow(row, bill)
        const billType = row.type ?? 'INV'
        return (
          <>
            {row.id > 0 && (
              <Stack direction="row" alignItems="center" justifyContent="flex-end">
                {showGstActions && billType === 'INV' && row.status !== 'CANCELLED' && (
                  <Tooltip title="More actions" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setNoteMenuAnchor(e.currentTarget)
                        setNoteMenuBill(row)
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {billType === 'INV' && !row.eInvoiceNo && (
                  <Tooltip title="Edit Bill" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedMonthlyBill(row)
                      }}
                    >
                      <EditCalendarIcon
                        fontSize="small"
                        sx={{ color: 'dodgerblue' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {showGstActions &&
                  (row.eInvoiceNo ? (
                    !isCancelledBillRow(row) && (
                      <Tooltip title="Cancel IRN" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setCancelEInvoiceBill(row)}
                        >
                          <HighlightOffRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  ) : (
                    <Tooltip
                      title={getGenerateEInvoiceTooltip(billType)}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() => setEInvoiceBill(row)}
                      >
                        <FactCheckRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ))}
                <Tooltip
                  title={row.eInvoiceNo ? 'View e-Invoice' : 'View Bill'}
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={() => handleViewBillPdf(row.id)}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Send on email" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleSendEmailBadgeClick(row.id)}
                    aria-label={`Send invoice on email, notification count ${row.notiCount ?? 0}`}
                    sx={{
                      width: 22,
                      height: 22,
                      minWidth: 22,
                      p: 0,
                      mx: 0.25,
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor:
                        (row.notiCount ?? 0) === 0 ? 'grey.500' : '#37A239',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor:
                          (row.notiCount ?? 0) === 0 ? 'grey.600' : '#2e8a32',
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        lineHeight: 1,
                        px: (row.notiCount ?? 0) > 9 ? 0.5 : 0,
                      }}
                    >
                      {row.notiCount ?? 0}
                    </Typography>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Bill" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={Boolean(row.eInvoiceNo)}
                      onClick={() => {
                        setShowDeleteDailog(true)
                      }}
                    >
                      <DeleteIcon
                        fontSize="small"
                        sx={{ color: row.eInvoiceNo ? 'gray' : 'crimson' }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
            {showDeleteDailog && (
              <DeleteMonthlyBill
                bill={row}
                onClose={(result) => {
                  if(result) {
                    if(bill != null && bill != undefined) {
                      dispatchAction(fetchMonthlyBillsAction(bill.id))
                    } else {
                      fetchBills()
                    }
                  }
                  setShowDeleteDailog(false)
                }}
              />
            )}
          </>
        )
      },
    },
  ]

  const createBillClickHandler = () => {
    if (bill.isRecurringEnabled) {
      setShowCreateMonthlyBill(true)
    } else {
      setShowCreateBill(true)
    }
  }
  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          backgroundColor: '#f0f0f0',
          height: 46,
          alignItems: 'center',
          display: 'flex',
          borderRadius: 0,
          padding: 0,
        }}
      >
        <GridToolbarExport
          startIcon={<PictureAsPdfIcon />}
          csvOptions={{ disableToolbarButton: true }}
          printOptions={{
            hideFooter: true,
          }}
        />
        <BarButton
          id="button_add_recurring_bill"
          title="Create Bill"
          startIcon={<AddCircleIcon />}
          onClick={createBillClickHandler}
        ></BarButton>
        <BarButton
          title="Bill Settings"
          id="id_bill_settings"
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => {
            setShowBillSettings(true)
          }}
        ></BarButton>
        <Box
          sx={{ textAlign: 'right', alignItems: 'flex-end', flex: 1, pr: 1.2 }}
        >
          Ac: {bill.id},Party Name: {bill.customer.name}, Site Address:{' '}
          {bill.siteAddress}
        </Box>
      </GridToolbarContainer>
    )
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          '& .super-app-theme--header': {
            backgroundColor: '#f9f9f9',
            color: 'black',
          },
          '& .MuiDataGrid-row': {
            position: 'relative',
            '& .MuiDataGrid-cell': {
              borderTop: 'none',
              borderBottom: 'none',
            },
          },
          '& .MuiDataGrid-row.monthly-bill-row-inv::before, & .MuiDataGrid-row.monthly-bill-row-note::before, & .MuiDataGrid-row.monthly-bill-row-summary::before':
            {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              height: '1px',
              backgroundColor: 'divider',
              pointerEvents: 'none',
              zIndex: 1,
            },
          '& .MuiDataGrid-row.monthly-bill-row-inv::before': {
            left: 0,
          },
          '& .MuiDataGrid-row.monthly-bill-row-note::before': {
            left: `${NOTE_ROW_DIVIDER_INDENT_PX}px`,
            backgroundColor: NOTE_ROW_DIVIDER_COLOR,
          },
          '& .MuiDataGrid-row.monthly-bill-row-summary::before': {
            left: 0,
          },
          '& .MuiDataGrid-row.monthly-bill-row-cancelled .MuiDataGrid-cell': {
            textDecoration: 'line-through',
            color: 'text.secondary',
          },
          '& .MuiDataGrid-row:first-of-type::before': {
            display: 'none',
          },
        }}
      >
        {viewPdfLoading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 10 }}>
            <Loader />
          </Box>
        )}
        {billsResponse && billsResponse.bills.length === 0 && (
          <EmptyWithAddButton
            onClickAdd={createBillClickHandler}
            buttonTitle="CREATE BILL"
            message="No bill exists for this account. Please click this button to create a monthly bill."
          />
        )}

        {billsResponse && billsResponse.bills.length > 0 && (
          <>
            {billsResponse && billsResponse.warning && (
                <BillingStatusBanner
                  message={billsResponse.warning}
                  messageType={'WARNING'}
                  showFixError={false}
                  onFixError={() => {} }
                  onDismiss={() => {}}
                />
             
            )}
            {billsResponse &&
              billsResponse.totalMismatchWarning &&
              showMismatchBanner && (
                <BillingStatusBanner
                  message={billsResponse.totalMismatchWarning}
                  messageType={billsResponse.messageType}
                  showFixError={billsResponse.billingError === true}
                  onFixError={() => setShowAdjustDialog(true)}
                  onDismiss={() => setShowMismatchBanner(false)}
                />
              )}
            <DataGrid
              rows={billsResponse.bills}
              rowHeight={BILL_TABLE_ROW_HEIGHT}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              getRowClassName={(params) =>
                getBillRowClassName(params.row as MonthlyBill)
              }
              sx={{
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: 'transparent',
                },
                '& .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-cell': {
                  py: 0.25,
                  userSelect: 'none',
                },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell[data-field="intervalName"]': {
                  display: 'flex',
                  alignItems: 'stretch',
                  overflow: 'visible',
                },
                '@media print': {
                  '& .MuiDataGrid-main': {
                    zoom: '0.75',
                    fontSize: 16,
                  },
                },
              }}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              pageSizeOptions={[20, 50]}
            />
          </>
        )}
      </Box>
      {(showCreateMonthlyBill || selectedMonthlyBill) && (
        <CreateMonthlyBill
          onClose={() => {
            setShowCreateMonthlyBill(false);
            setSelectedMonthlyBill(null);
          }}
          account={bill}
          monthlyBill={selectedMonthlyBill}
        />
      )}

      {showCreateBill && (
        <CreateBill onClose={() => setShowCreateBill(false)} account={bill} />
      )}

      {showBillSettings && (
        <BillSettings
          onClose={() => setShowBillSettings(false)}
          account={bill}
        />
      )}
      <Popover
        open={Boolean(noteMenuAnchor)}
        anchorEl={noteMenuAnchor}
        onClose={() => setNoteMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List sx={{ p: 0 }} component="nav">
          <ListItem>
            <Button
              sx={{ fontWeight: 400 }}
              onClick={() => {
                setNoteMenuAnchor(null)
                if (noteMenuBill) {
                  setNoteDialog({
                    invoiceId: noteMenuBill.id,
                    noteType: 'DBN',
                    accountId: noteMenuBill.accountId ?? bill?.id ?? 0,
                  })
                }
              }}
            >
              Create Debit Note (+)
            </Button>
          </ListItem>
          <ListItem>
            <Button
              sx={{ fontWeight: 400 }}
              onClick={() => {
                setNoteMenuAnchor(null)
                if (noteMenuBill) {
                  setNoteDialog({
                    invoiceId: noteMenuBill.id,
                    noteType: 'CRN',
                    accountId: noteMenuBill.accountId ?? bill?.id ?? 0,
                  })
                }
              }}
            >
              Create Credit Note (−)
            </Button>
          </ListItem>
        </List>
      </Popover>

      {noteDialog && (
        <DebitCreditNoteDialog
          open
          invoiceId={noteDialog.invoiceId}
          accountId={noteDialog.accountId}
          noteType={noteDialog.noteType}
          onClose={() => setNoteDialog(null)}
          onSuccess={() => {
            if (bill?.id) {
              dispatchAction(fetchMonthlyBillsAction(bill.id))
            } else {
              fetchBills()
            }
          }}
        />
      )}

      {eInvoiceBill && (
        <GenerateEInvoice
          eInvoiceId={eInvoiceBill.id}
          accountId={eInvoiceBill.accountId}
          onClose={() => {
            setEInvoiceBill(null)
            if (bill) {
              dispatchAction(fetchMonthlyBillsAction(bill.id))
            } else {
              fetchBills()
            }
          }}
        />
      )}

      {cancelEInvoiceBill?.eInvoiceNo && (
        <CancelEInvoice
          irn={cancelEInvoiceBill.eInvoiceNo}
          accountId={cancelEInvoiceBill.accountId}
          onClose={() => {
            setCancelEInvoiceBill(null)
            if (bill) {
              dispatchAction(fetchMonthlyBillsAction(bill.id))
            } else {
              fetchBills()
            }
          }}
        />
      )}

      <BulkInvoiceActionConfirm
        open={showBulkConfirm}
        action={pendingBulkAction}
        count={emailActionInvoiceIds?.length ?? 0}
        loading={bulkActionLoading}
        onClose={() => {
          if (bulkActionLoading) {
            return
          }
          setShowBulkConfirm(false)
          setPendingBulkAction(null)
          setEmailActionInvoiceIds(null)
        }}
        onConfirm={handleBulkActionConfirm}
      />

      {showAdjustDialog && (
        <ConfirmationAppDialog
          id="adjust-billing-gap-dialog"
          title="Invoice Difference Detected"
          onClose={() => {
            if (!adjusting) {
              setShowAdjustDialog(false)
            }
          }}
          close={false}
          dialogActionsButtons={[
            {
              text: 'Cancel',
              color: 'success',
              variant: 'outlined',
              onClickHandler: () => {
                setShowAdjustDialog(false)
              },
            },
            {
              text: adjusting ? 'Adjusting...' : 'Adjust',
              color: 'warning',
              variant: 'contained',
              onClickHandler: adjustBillingGapHandler,
            },
          ]}
        >
          <>
            <Typography>
              This issue happens when the rate or quantity is changed after
              invoices are created.
            </Typography>
            <br />
            <Typography fontWeight={700}>Recommended Fix:</Typography>
            <Typography>
              Recreate the invoices to automatically correct the difference.
            </Typography>
            <br />
            <Typography>
              If the invoices have already been issued and cannot be recreated,
              you can continue with adjustment. The system will adjust this
              difference internally without changing any invoice data or
              accounting entries.
            </Typography>
            <br />
            <Typography>
              The current difference will be ignored, but if a new difference
              occurs in the future, the system will show this warning again.
            </Typography>
          </>
        </ConfirmationAppDialog>
      )}
    </>
  )
}

export default MonthlyBills
